import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config/api';

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing...');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));

    // New Google OAuth flow: direct session_token from backend redirect
    const directToken = params.get('session_token');
    if (directToken) {
      processGoogleAuth(directToken);
      return;
    }

    // Legacy Emergent flow: session_id exchange
    const sessionId = params.get('session_id');
    if (sessionId) {
      processEmergentSession(sessionId);
      return;
    }

    // Check URL params for auth errors (from failed Google OAuth)
    const urlParams = new URLSearchParams(window.location.search);
    const authError = urlParams.get('auth_error');
    if (authError) {
      setError(`Sign in failed: ${authError.replace(/_/g, ' ')}. Please try again.`);
      setTimeout(() => navigate('/', { replace: true }), 3000);
      return;
    }

    // No auth data found
    setError('No authentication data found. Please try signing in again.');
    setTimeout(() => navigate('/', { replace: true }), 3000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle the new Google OAuth flow.
   * The backend already exchanged the code and created a session.
   * We just need to save the token and redirect.
   */
  const processGoogleAuth = async (token) => {
    setStatus('Completing sign in...');

    // Save session token to localStorage (mobile fallback + impersonation support)
    localStorage.setItem('session_token', token);

    // Auto-apply pending referral code (from /join/:code page)
    const pendingReferral = localStorage.getItem('pending_referral_code');
    if (pendingReferral) {
      try {
        await xhrPost(
          `${BACKEND_URL}/api/billing/referral/apply`,
          JSON.stringify({ code: pendingReferral }),
          { 'Authorization': `Bearer ${token}` }
        );
      } catch {
        // Silently fail — user can still use the app
      }
      localStorage.removeItem('pending_referral_code');
    }

    setStatus('Success! Redirecting...');
    await new Promise(r => setTimeout(r, 400));
    window.location.href = '/stash';
  };

  /**
   * Legacy: Exchange Emergent session_id for an app session.
   */
  const processEmergentSession = async (sessionId, attempt = 1) => {
    const maxRetries = 3;

    try {
      setStatus(attempt > 1 ? `Retrying... (${attempt}/${maxRetries})` : 'Exchanging credentials...');

      const url = `${BACKEND_URL}/api/auth/session`;
      const bodyStr = JSON.stringify({ session_id: sessionId });

      let responseData;

      try {
        responseData = await xhrPost(url, bodyStr);
      } catch (fetchErr) {
        if (attempt < maxRetries) {
          setRetryCount(attempt);
          await new Promise(r => setTimeout(r, 1200));
          return processEmergentSession(sessionId, attempt + 1);
        }
        throw new Error(fetchErr.message || 'Connection failed');
      }

      if (responseData.ok) {
        const userData = responseData.data;

        if (userData.session_token) {
          localStorage.setItem('session_token', userData.session_token);
        }

        localStorage.setItem('user_data', JSON.stringify({
          user_id: userData.user_id,
          email: userData.email,
          name: userData.name,
          picture: userData.picture
        }));

        setStatus('Success! Redirecting...');
        await new Promise(r => setTimeout(r, 400));

        // Auto-apply pending referral code
        const pendingReferral = localStorage.getItem('pending_referral_code');
        if (pendingReferral && userData.session_token) {
          try {
            await xhrPost(
              `${BACKEND_URL}/api/billing/referral/apply`,
              JSON.stringify({ code: pendingReferral }),
              { 'Authorization': `Bearer ${userData.session_token}` }
            );
            localStorage.removeItem('pending_referral_code');
          } catch {
            // Silently fail
          }
          localStorage.removeItem('pending_referral_code');
        }

        window.location.href = '/stash';

      } else {
        const errorDetail = responseData.data?.detail || 'Session exchange failed';

        if (responseData.status === 401 && errorDetail.includes('already been used')) {
          const existingToken = localStorage.getItem('session_token');
          if (existingToken) {
            window.location.href = '/stash';
            return;
          }
        }

        setError(errorDetail);
        setTimeout(() => navigate('/', { replace: true }), 3000);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
      setTimeout(() => navigate('/', { replace: true }), 3000);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center px-4">
          <div className="text-red-500 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-charcoal font-medium mb-2">{error}</p>
          <p className="text-gray-500 text-sm">Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage mx-auto"></div>
        <p className="mt-4 text-charcoal">{status}</p>
        {retryCount > 0 && (
          <p className="mt-2 text-gray-500 text-sm">Connection issues detected. Retrying...</p>
        )}
      </div>
    </div>
  );
};

/**
 * XMLHttpRequest-based POST — avoids iOS Safari's "object cannot be cloned"
 * DOMException that can occur with fetch() + credentials on cross-origin.
 */
function xhrPost(url, bodyStr, extraHeaders = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Accept', 'application/json');
    Object.entries(extraHeaders).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.withCredentials = true;
    xhr.timeout = 20000;

    xhr.onload = function () {
      let data;
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        data = { detail: xhr.responseText };
      }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, data });
    };

    xhr.onerror = function () {
      reject(new Error('Network request failed'));
    };

    xhr.ontimeout = function () {
      reject(new Error('Request timed out'));
    };

    xhr.send(bodyStr);
  });
}

export default AuthCallback;
